import {
  ModelSelect, Flex, P, Button, ModelTextarea, FlexColumn, InputRow, EmptyInput,
  ModelType, SecondaryButton,
  useAsyncFunctionProgress,
} from '@equifinance/shared';
import styled from 'styled-components';
import { formatDateTime } from '@equifinance/shared/utils';
import { useEffect } from 'react';
import {
  useUnderwriters, ChecklistItem, Status, useChecklistContext,
} from '../../../contexts';

export const SubmitContainer = styled(Flex)`${({ theme }) => `
  align-items: center;
  justify-content: end;
  min-height: ${theme.fontSize[10]};
`}`;

export const InfoPanel = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[3]};
  padding: ${theme.padding[3]} ${theme.padding[4]};
`}`;

export const Divider = styled.div`${({ theme }) => `
  width: ${theme.borderWidth[1]};
  height: ${theme.padding[5]};
  background: ${theme.colours.neutral[6]};
`}`;

export const SubmittedText = styled.p`${({ theme }) => `
  font-family: ${theme.fontFamily[1]};
  color: ${theme.colours.neutral[2]};
  font-weight: ${theme.fontWeight.normal};
  font-size: ${theme.fontSize[2]};
`}`;

export const UnderwriterDecisionContainer = styled(FlexColumn)
<{ status?: keyof typeof Status }>`${({ theme, status }) => `gap: ${
  status === 'AwaitingDecision' ? theme.padding[6] : theme.padding[2]};
`}`;

const InputRowBlock = styled(InputRow)`
  padding-block: 0;
`;

export const UnderwriterDecisionTitle = styled(Flex)
<{hasUnderwriterUpdateForm?: boolean}>`
  ${({ theme, hasUnderwriterUpdateForm = false }) => `
    min-height: ${theme.padding[10]};
    justify-content: space-between;
    align-items: center;
    padding: ${theme.padding[1]} ${theme.padding[5]};
    border-radius: ${theme.padding[2]} ${theme.padding[2]} ${
  hasUnderwriterUpdateForm
    ? '0 0'
    : theme.padding[2]};
    background: ${theme.colours.primary[3]};
`}`;

export const UnderwriterDecisionForm = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[6]};
  padding: ${theme.padding[5]} ${theme.padding[5]};
  border-radius: 0 0 ${theme.borderRadius[2]} ${theme.borderRadius[2]};
  background: ${theme.colours.neutral[9]};
  box-shadow:
    0px 1px 3px 0px rgba(16, 24, 40, 0.10),
    0px 1px 2px 0px rgba(16, 24, 40, 0.06);
`}`;

export const Label = styled(P)`${({ theme }) => `
  font-family: 'Montserrat';
  color: ${theme.colours.neutral[10]};
  font-size: ${theme.fontSize[3]};
  font-weight: ${theme.fontWeight.bolder};
`}`;

const InteractiveControls = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[3]};
  justify-content: flex-end;
`}`;

const IconButton = styled(Button)`${({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.padding[3]};
  border-radius: ${theme.borderRadius[2]};
  border: 1px solid ${theme.colours.neutral[10]};
  background: ${theme.colours.primary[3]};
`}`;

const Icon = styled.i`${({ theme }) => `
  color: ${theme.colours.neutral[10]};
`}`;

export const SelectWrapper = styled.div`${({ theme }) => `
  height: ${theme.padding[9]};
  width: ${theme.dropdownWidth};
`}`;

export enum UnderwriterDecisionOptions {
  AutoApproved = 'Auto-approved',
  Override = 'Override',
}

export type UnderwriterDecisionActions = 'pencil' | 'remove';

type UnderwriterDecisionProps = {
  field: string;
  options: Record<string, string>;
  checklistItem?: ChecklistItem;
  decisionDisabled?: boolean;
  submittedSectionDisabled?: boolean;
  required?: boolean;
  showPencilIcon?: boolean;
  showRemoveIcon?: boolean;
  onAction?: (action: UnderwriterDecisionActions) => Promise<void>;
  onSubmit?: () => Promise<void>;
  onCancel?: () => void;
};

export function UnderwriterDecision({
  field,
  options = UnderwriterDecisionOptions,
  checklistItem,
  decisionDisabled = false,
  submittedSectionDisabled = true,
  required = false,
  showPencilIcon = false,
  showRemoveIcon = false,
  onAction = async () => {},
  onSubmit = async () => {},
  onCancel,
}: UnderwriterDecisionProps) {
  const { underwriters, fetchUnderwriters } = useUnderwriters();
  const {
    id, updateChecklist,
  } = useChecklistContext();

  const {
    status,
    canSubmit,
    submittedByName,
    submittedTimestamp,
    checklistIndicatorType,
    submitted,
  } = checklistItem ?? {};

  const isSubmittedDisable = submittedSectionDisabled && submitted;

  const showUnderwriterUpdateForm = Object.keys(checklistItem ?? {}).length > 0;

  const underwriterOptions: Record<string, string> = underwriters
    .filter((underwriter) => underwriter.isActive)
    .reduce((prev, current) => ({
      ...prev,
      [current.id]: current.displayName,
    }), {});

  const formDisabled = status === 'Overridden'
    || checklistIndicatorType === 'Locked'
    || isSubmittedDisable;

  const onChangeAuthorisedByName = (model: ModelType) => {
    const authorisedById = model[`${field}.authorisedById`];
    const authorisedByName = underwriterOptions[authorisedById];

    updateChecklist(`${field}.authorisedById`, authorisedById);
    updateChecklist(`${field}.authorisedByName`, authorisedByName);
  };

  useEffect(() => {
    fetchUnderwriters();
  }, [id]);

  const onActionClicked = () => onAction(showPencilIcon ? 'pencil' : 'remove');

  const { inProgress, execute: executeAction } = useAsyncFunctionProgress(onActionClicked);

  const { inProgress: submitInProgress, execute: executeSubmit } = useAsyncFunctionProgress(onSubmit);

  return (
    <UnderwriterDecisionContainer status={status}>
      <div>
        <UnderwriterDecisionTitle
          hasUnderwriterUpdateForm={showUnderwriterUpdateForm}
        >
          <Label>{'Underwriter\'s decision'}</Label>
          <InteractiveControls>
            {(showPencilIcon || showRemoveIcon) && (
            <IconButton
              onClick={executeAction}
              disabled={checklistIndicatorType === 'Locked' || isSubmittedDisable || inProgress}
            >
              <Icon
                className={`${showPencilIcon ? 'mi-edit' : 'mi-close'}`}
              />
            </IconButton>
            )}
            <SelectWrapper>
              <ModelSelect
                required={required}
                placeholder="Please select"
                field={`${field}.decision`}
                options={options}
                disabled={decisionDisabled}
              />
            </SelectWrapper>
          </InteractiveControls>
        </UnderwriterDecisionTitle>
        {(showUnderwriterUpdateForm && status !== 'AutoApproved') && (
          <UnderwriterDecisionForm>
            <ModelTextarea
              field={`${field}.justification`}
              label="Justification"
              required
              rows={3}
              disabled={formDisabled}
            />
            <InputRowBlock>
              <ModelSelect
                label="Authorised by"
                field={`${field}.authorisedById`}
                placeholder="Please select"
                options={underwriterOptions}
                updateOverride={onChangeAuthorisedByName}
                required
                disabled={formDisabled}
              />
              <EmptyInput />
              <EmptyInput />
            </InputRowBlock>
          </UnderwriterDecisionForm>
        )}
      </div>
      {showUnderwriterUpdateForm && (
        <SubmitContainer>
          {status === 'AwaitingDecision' && (
          <InteractiveControls>
            <SecondaryButton
              small
              onClick={onCancel}
              disabled={checklistIndicatorType === 'Locked' || isSubmittedDisable}
            >
              Cancel
            </SecondaryButton>
            <Button
              small
              onClick={executeSubmit}
              disabled={!canSubmit || submitInProgress}
            >
              Submit
            </Button>
          </InteractiveControls>
          )}
          {status === 'Overridden' && (
          <InfoPanel>
            <SubmittedText>
              {submittedByName}
            </SubmittedText>
            <Divider />
            <SubmittedText>
              {formatDateTime(submittedTimestamp, 'DD/MM/YYYY, HH:mm:ss')}
            </SubmittedText>
          </InfoPanel>
          )}
        </SubmitContainer>
      )}
    </UnderwriterDecisionContainer>
  );
}
